import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import { Field, Select, TextInput } from '../../components/ui/Field';
import Toggle from '../../components/ui/Toggle';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  createCouponCode,
  getLiveAgentList,
  getReportTypesForCoupon,
  getSelectedCouponDetails,
  updateCouponCode,
} from '../../actions/couponAction';
import countries from '../../static/countries.json';
import './growth.css';

const TYPES = [
  { value: 'percentage', label: 'Percentage off' },
  { value: 'value', label: 'Flat amount off' },
];

const PLATFORMS = [
  { value: 'all', label: 'All platforms' },
  { value: 'web', label: 'Web' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

const EMPTY = {
  code: '',
  coupon_type: 'percentage',
  coupon_value: '',
  max_value: '',
  limit: 100,
  platform_type: 'all',
  start_datetime: '',
  end_datetime: '',
  is_private: false,
};

export default function CouponForm() {
  const { action } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, toast } = useOverlays();

  const isNew = action === 'add';
  const [form, setForm] = useState(EMPTY);
  const [markets, setMarkets] = useState([]);
  const [reports, setReports] = useState([]);
  const [agents, setAgents] = useState([]);
  const [applicableReports, setApplicableReports] = useState([]);
  const [applicableAgents, setApplicableAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const countryOptions = useMemo(
    () =>
      (Array.isArray(countries) ? countries : []).map((c) => ({
        value: c.code || c.iso_2_code || c.name,
        label: c.name,
      })),
    []
  );

  useEffect(() => {
    getReportTypesForCoupon()
      .then((list) => setReports((list || []).map((r) => ({ id: String(r.id), name: r.report_name }))))
      .catch(() => setReports([]));
    getLiveAgentList()
      .then((list) => {
        const rows = Array.isArray(list) ? list : list?.items || [];
        setAgents(rows.map((a) => ({ id: String(a.id), name: a.properties?.name || a.name })));
      })
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    getSelectedCouponDetails(action)
      .then((c) => {
        if (!c?.id) return;
        setForm({
          code: c.code || '',
          coupon_type: c.coupon_type || 'percentage',
          coupon_value: c.coupon_value ?? '',
          max_value: c.max_value ?? '',
          limit: c.limit ?? 100,
          platform_type: c.platform_type || 'all',
          start_datetime: (c.start_date || '').slice(0, 16),
          end_datetime: (c.end_date || '').slice(0, 16),
          is_private: !!c.is_private,
        });
        setMarkets(c.available_countries || []);
        setApplicableReports((c.applicable_for?.reports || []).map((r) => String(r.id)));
        setApplicableAgents((c.applicable_for?.consultants || []).map((r) => String(r.id)));
      })
      .catch(() => toast('Could not load the coupon', { tone: 'error' }));
  }, [action, isNew, toast]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const toggleIn = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const validate = () => {
    const next = {};
    if (!form.code.trim()) next.code = 'A code is required';
    if (!form.coupon_value) next.coupon_value = 'Enter a value';
    if (form.coupon_type === 'percentage' && Number(form.coupon_value) > 100)
      next.coupon_value = 'A percentage cannot exceed 100';
    if (!form.start_datetime) next.start_datetime = 'Required';
    if (!form.end_datetime) next.end_datetime = 'Required';
    if (form.start_datetime && form.end_datetime && form.end_datetime <= form.start_datetime)
      next.end_datetime = 'Must be after the start';
    if (markets.length === 0) next.markets = 'Pick at least one market';
    if (applicableReports.length === 0 && applicableAgents.length === 0)
      next.applicable = 'Pick at least one report or consultant';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = useCallback(async () => {
    if (!validate()) return;

    const ok = await confirm({
      title: isNew ? `Create ${form.code}?` : `Update ${form.code}?`,
      body: isNew
        ? `${form.code} becomes usable from its start date, up to ${form.limit} redemptions.`
        : `Changes to ${form.code} take effect immediately for anyone who has not yet redeemed it.`,
      confirmLabel: isNew ? 'Create coupon' : 'Save changes',
    });
    if (!ok) return;

    setBusy(true);
    const payload = {
      ...form,
      coupon_value: String(form.coupon_value),
      max_value: form.max_value ? String(form.max_value) : null,
      limit: Number(form.limit),
      countries: markets,
      applicable_for: {
        reports: applicableReports.map((id) => ({ id })),
        consultants: applicableAgents.map((id) => ({ id })),
      },
    };

    try {
      if (isNew) await createCouponCode(dispatch, payload);
      else await updateCouponCode(dispatch, action, payload);
      navigate('/coupon-promotion');
    } catch {
      /* the action already toasted */
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, markets, applicableReports, applicableAgents, isNew, action, dispatch, navigate, confirm]);

  return (
    <>
      <PageHeader
        section="Growth"
        title={isNew ? 'New coupon' : `Edit ${form.code || 'coupon'}`}
        back="coupons"
        backTo="/coupon-promotion"
      />

      <div className="sv-screen-body">
        <div className="sv-invite">
          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Code &amp; type</h2>
            <div className="sv-invite__grid">
              <Field label="Code" required error={errors.code} className="sv-span-2">
                <TextInput
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="WELCOME25"
                  invalid={!!errors.code}
                />
              </Field>
              <Field label="Type" required>
                <Select options={TYPES} value={form.coupon_type} onChange={set('coupon_type')} />
              </Field>
              <Field
                label={form.coupon_type === 'percentage' ? 'Percentage' : 'Amount'}
                required
                error={errors.coupon_value}
              >
                <TextInput
                  type="number"
                  value={form.coupon_value}
                  onChange={set('coupon_value')}
                  invalid={!!errors.coupon_value}
                />
              </Field>
              {form.coupon_type === 'percentage' && (
                <Field label="Max discount" hint="Caps the rupee value" className="sv-span-2">
                  <TextInput type="number" value={form.max_value} onChange={set('max_value')} />
                </Field>
              )}
            </div>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Limits</h2>
            <div className="sv-invite__grid">
              <Field label="Total redemptions" required>
                <TextInput type="number" value={form.limit} onChange={set('limit')} />
              </Field>
              <Field label="Platform">
                <Select options={PLATFORMS} value={form.platform_type} onChange={set('platform_type')} />
              </Field>
              <div className="sv-rail-toggle sv-span-2" style={{ marginTop: 6 }}>
                <span>Private coupon (not shown publicly)</span>
                <Toggle
                  checked={form.is_private}
                  onChange={(v) => setForm((f) => ({ ...f, is_private: v }))}
                  label="Private"
                />
              </div>
            </div>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Eligibility</h2>
            <Field label="Markets" required error={errors.markets}>
              <div className="sv-role-grid" style={{ maxHeight: 180, overflowY: 'auto' }}>
                {countryOptions.map((c) => (
                  <label className="sv-role-option" key={c.value}>
                    <input
                      type="checkbox"
                      className="sv-checkbox"
                      checked={markets.includes(c.value)}
                      onChange={() => toggleIn(markets, setMarkets, c.value)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </Field>

            <div style={{ height: 14 }} />

            <Field label="Applicable to" required error={errors.applicable}>
              <div className="sv-role-grid" style={{ maxHeight: 200, overflowY: 'auto' }}>
                {reports.map((r) => (
                  <label className="sv-role-option" key={`r-${r.id}`}>
                    <input
                      type="checkbox"
                      className="sv-checkbox"
                      checked={applicableReports.includes(r.id)}
                      onChange={() => toggleIn(applicableReports, setApplicableReports, r.id)}
                    />
                    {r.name}
                  </label>
                ))}
                {agents.map((a) => (
                  <label className="sv-role-option" key={`a-${a.id}`}>
                    <input
                      type="checkbox"
                      className="sv-checkbox"
                      checked={applicableAgents.includes(a.id)}
                      onChange={() => toggleIn(applicableAgents, setApplicableAgents, a.id)}
                    />
                    {a.name}
                  </label>
                ))}
              </div>
            </Field>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Schedule</h2>
            <div className="sv-invite__grid">
              <Field label="Starts" required error={errors.start_datetime}>
                <TextInput
                  type="datetime-local"
                  value={form.start_datetime}
                  onChange={set('start_datetime')}
                  invalid={!!errors.start_datetime}
                />
              </Field>
              <Field label="Ends" required error={errors.end_datetime}>
                <TextInput
                  type="datetime-local"
                  value={form.end_datetime}
                  onChange={set('end_datetime')}
                  invalid={!!errors.end_datetime}
                />
              </Field>
            </div>
          </section>

          <div className="sv-invite__foot">
            <Button variant="secondary" onClick={() => navigate('/coupon-promotion')}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} loading={busy}>
              {isNew ? 'Create coupon' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
